import React, { useEffect, useState, useRef, useMemo } from 'react';
import Axios from 'axios';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Typography,
  Form,
  Input,
  Select,
  TreeSelect,
  Button,
  Row,
  Col,
  Space,
  Tooltip,
  DatePicker,
  InputNumber,
  Switch,
  Card,
  Modal,
  Alert,
} from 'antd';
import DataTable from '@/components/DataTable';
import OrderInfoFields, { orderPayload, packagingPayload } from '@/components/OrderInfoFields';
import BatchPurposeField from '@/components/BatchPurposeField';
import { PlusOutlined, RetweetOutlined } from '@ant-design/icons';
import { Factory, Package, Check, PackageCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import MainCard from '@/components/MainCard';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import {
  bp_batch_configuration,
  get_master_batchs,
  batch_configuration_combine,
  get_recipe_for_schedule,
  get_product_list_both,
  get_api_function,
  get_recipe_by_product_id,
  get_completed_batches_by_recipe,
  get_completed_batches_by_source_recipe,
  get_recipe_process_details,
  bp_batch_configuration_pharma,
  get_process_control_parameter,
  get_bill_of_materials,
} from '../../../utils/api';
import { useSelector, useDispatch } from 'react-redux';
import DualESignatureAuth from '@/components/DualESignatureAuth';
import { accessControlUtils } from '@/components/AccessControlUtils';
import useAppSetting, { useMbmrAlias, useMonitoringAlias } from '@/hooks/useAppSetting';
import {
  resolveRecipeById,
  filterFinalizedRecipes,
  buildVersionedRecipeSelectOptions,
} from '@/utils/recipe/recipeSelectOptions';
import SapBomGate from '@/components/batch/SapBomGate';
const { Text } = Typography;

function Scheduling({
  refresh,
  set_refresh,
  selectedButton,
  setIsBomVisible,
  set_view_bom_data,
  setMinMaxBatchSize,
  setBatchSize,
  batch_type,
  masterBatch,
  selectedLots,
  setSelectedLots,
  onSuccess,
  standalone,
}) {
  // Two ways this component is rendered:
  //   • embedded  — inside the "Issue Batch" / "Issue Lot" modal on /batches,
  //     which owns the BOM drawer, the batch-size mirror and the list refresh
  //     and passes them down as props. Layout must stay exactly as it was.
  //   • standalone — the "Issue Batch" menu item (/scheduling). No parent, so
  //     the page has to supply its own header, card shell and BOM viewer.
  // The route sets `standalone`; the `set_refresh` fallback covers any other
  // render that has no parent wiring at all.
  const isStandalone = standalone ?? typeof set_refresh !== 'function';
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [recipe_data, set_recipe_data] = useState([]);
  // recipe_ids that already have Recipe_Status (finalized). Used to filter the
  // product MBMR list against the live API, which ignores `finalized_only`.
  const finalizedRecipeIdsRef = useRef(new Set());
  const productRecipesRef = useRef([]);
  const [machine_data, set_machine_data] = useState([]);
  const [selectedMachineKeys, setSelectedMachineKeys] = useState([]);
  const [batch_list, set_batch_list] = useState([]);
  const [product_list, set_product_list] = useState([]);
  const [selected_recipe, set_selected_recipe] = useState();
  // What we are scheduling: 'BMR' (manufacturing, default) or 'BPR' (packaging).
  const [scheduleMode, setScheduleMode] = useState('BMR');
  // For BPR: completed batches of the selected packaging recipe's SOURCE MBMR,
  // shown in the "Source Master Batch" dropdown.
  const [sourceBatchList, setSourceBatchList] = useState([]);
  // (No role_list: the "Process Role Assignment" table that consumed it is commented out further
  //  down, so the bp_get_role call it needed was removed — see the useEffect below.)
  // All Control & Monitoring Parameters configured across every machine in
  // Add Equipment (not filtered to one machine) — shown when Continuous
  // Monitoring is toggled on for this batch release.
  const [monitoring_parameter_list, set_monitoring_parameter_list] = useState([]);
  // Approved/submitted demands from the demand board (/create-demand). Read-only here: the screen
  // that issues a batch had no idea the demand board existed (`grep -ic demand` was 0), so a
  // planner read a product, a recipe and a quantity off one page and retyped them into this one.
  // This shows him what was planned for the product he just picked. It does NOT yet RECORD which
  // demand a batch was issued against — that needs a column on BatchOrderInfo and a migration,
  // which is deliberately out of this lane (see the report).
  const [demand_list, set_demand_list] = useState([]);
  const [batchNumberLoading, setBatchNumberLoading] = useState(false);
  // E-Signature state
  const [showESignatureModal, setShowESignatureModal] = useState(false);
  const [pendingBatchData, setPendingBatchData] = useState(null);
  const isESign = accessControlUtils('ESign')?.create_access || false;
  const isMasterLotVisible = useAppSetting('Master/Lot Visibility', true);
  const isContinuousMonitoringVisible = useAppSetting(
    'Continuous Monitoring Visibility',
    true
  );
  // When enabled, the auto-generated batch number can be edited on this page.
  const isBatchNumberEditable = useAppSetting('Batch Number Editable', false);

  const selectedModule = useSelector((state) => state.moduleView);
  // Test/Production comes from the header toggle. Embedded, /batches passes its
  // own copy down; standalone we read the same redux slice the header writes.
  const testProdView = useSelector((state) => state.testProdView);
  // Standalone BOM viewer — /batches owns a BOM modal (with lot picking) and
  // passes setIsBomVisible/set_view_bom_data; on its own the page shows a
  // read-only material list instead of crashing on the missing setters.
  const [localBom, setLocalBom] = useState({ open: false, loading: false, rows: [], batch_size: 0 });
  // A failed release gate comes back as 409 + `release_gate` (a {check: bool} map)
  // or a pre-flight `items` list — shown as a check list instead of a bare toast.
  const [releaseGate, setReleaseGate] = useState(null);
  // 2026-10-12 · the equipment-cleaning refusal, machine by machine, each with a way to go and clean it.
  const [cleaningBlock, setCleaningBlock] = useState(null);   // { message, machines: [gate line] }
  const mbmrAlias = useMbmrAlias();
  const monitoringAlias = useMonitoringAlias();
  const [initial_values, set_initial_values] = useState({
    master_lot: false,
    product: '',
    recipe_id: masterBatch?.recipe_id,   // the page is also opened standalone from the menu (no masterBatch prop)
    batch: '',
    // BPR only: chosen source-MBMR completed batch this packaging batch packs.
    source_master_batch: null,
    // schedule_time: "",
    batch_size: '',
    machine_id: [],
    sub_machine_ids: [],
    machine_data: [],
    batch_type: batch_type,
    continuous_monitoring: false,
    continuous_monitoring_parameter_ids: [],
    selectedModule: selectedModule,
    min_quantity: selected_recipe?.min_quantity || 0,
    max_quantity: selected_recipe?.max_quantity || 0,
    remainning_size: masterBatch?.remainning_size || 0,
  });
  // Initial form values

  //############### Handle API's ##################
  // Machine list is loaded on recipe selection, not on mount

  // The parent-owned setters below only exist when embedded in /batches —
  // standalone they are undefined, so every call site goes through these.
  const applyMinMaxBatchSize = (v) => {
    if (typeof setMinMaxBatchSize === 'function') setMinMaxBatchSize(v);
  };
  const applyBatchSize = (v) => {
    if (typeof setBatchSize === 'function') setBatchSize(v);
  };

  useEffect(() => {
    const recipeData = resolveRecipeById(recipe_data, selected_recipe?.recipe_id);
    set_selected_recipe(recipeData || null);
    if (recipeData) {
      applyMinMaxBatchSize({
        min: recipeData?.min_quantity,
        max: recipeData?.max_quantity,
        actual: recipeData?.master_batch_size,
      });
    }

  }, [selected_recipe?.recipe_id]);

  // recipe_type reflects the Test/Production toggle in the main header:
  // true when Production is selected, false for Test. Sent on every /batches API.
  // Sent as string 'true' (Production) / 'false' (Test) — backend parses it.
  // Embedded, /batches passes its own copy of the header toggle; standalone we
  // fall back to the redux slice the header writes to.
  const effectiveTestProd = selectedButton ?? testProdView;
  const recipeTypeFlag = effectiveTestProd === 'production';

  const get_master_batchs_data = async () => {
    try {
      await get_master_batchs({ recipe_type: recipeTypeFlag }, (res) => {
        if (res?.data.status === 'success') {
          set_batch_list(res?.data?.data);
        } else {
          set_batch_list([]);
        }
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    // bp_get_role used to be fetched here for the "Process Role Assignment" table below, which is
    // commented out — so it was a dead call whose only visible effect was a 403 for any role that
    // does not hold User Management (the Planner). It stays removed; if that table is ever
    // re-enabled, the permission question has to be answered rather than the call restored.
    //
    // No machine id passed => every Control & Monitoring Parameter across
    // every machine created in Add Equipment.
    get_process_control_parameter((res) => {
      set_monitoring_parameter_list(res?.data?.data || []);
    });
    get_api_function('/production-demand/list/', (res) => {
      set_demand_list(Array.isArray(res?.data?.data) ? res.data.data : []);
    });
  }, []);

  // Demands the planner could be issuing against: same product, still open (a demand the board
  // has approved, or one still awaiting approval — both are worth seeing), narrowed to the
  // selected master record once he has picked one.
  const demandsForSelection = (productId, recipeId) => {
    if (!productId) return [];
    return demand_list.filter(
      (d) =>
        Number(d.product_id) === Number(productId) &&
        ['Approved', 'Submitted'].includes(d.status) &&
        (!recipeId || d.recipe_id == null || Number(d.recipe_id) === Number(recipeId))
    );
  };

  const get_recipe = async () => {
    try {
      await get_product_list_both({ module_type: selectedModule, recipe_type: recipeTypeFlag }, success_bp_product_data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const get_recipe_by_productid = (product) => {
    try {
      // Do NOT send recipe_type=false in Test. The live get_recipe_by_product_id
      // treats the key's presence as "skip Recipe_Status" and returns drafts.
      // Omitting it uses the default finalized-only branch. Production still
      // sends recipe_type=true. finalized_only is for backends that support it.
      const params = { product_id: product, finalized_only: true };
      if (recipeTypeFlag) params.recipe_type = true;
      get_recipe_by_product_id(success_recipe_by_product, params);
    } catch (error) {
      console.log(error);
    }
  };

  // Load the completed batches of an upstream recipe so the user can pick
  // one — for BPR, the packaging recipe's OWN recipe_id is sent to
  // get_completed_batches_by_source_recipe, which resolves that recipe's
  // source MBMR internally on the backend; for an input-based recipe it's
  // the upstream recipe selected via "Select Recipe" in its metadata
  // (source_mbmr_id), sent to get_completed_batches_by_recipe. Same picker,
  // same source_master_batch field either way.
  const loadSourceBatches = (recipeData) => {
    if (scheduleMode === 'BPR') {
      if (!recipeData?.recipe_id) {
        setSourceBatchList([]);
        return;
      }
      get_completed_batches_by_source_recipe((res) => {
        if (res?.data?.status === 'success') {
          setSourceBatchList(res?.data?.data || []);
        } else {
          // Covers every documented failure case: recipe_id missing (400),
          // recipe not found (404), this recipe has no source_mbmr configured
          // (400), unauthenticated (401 — different shape, already normalized
          // by the api.js wrapper), and unexpected server errors (500).
          setSourceBatchList([]);
          toast.error(
            res?.data?.message || 'Failed to load completed batches for the source recipe'
          );
        }
      }, { recipe_id: recipeData.recipe_id, recipe_type: recipeTypeFlag });
      return;
    }
    const sourceMbmrId = recipeData?.source_mbmr_id;
    if (!sourceMbmrId) {
      setSourceBatchList([]);
      return;
    }
    get_completed_batches_by_recipe((res) => {
      const rows = res?.data?.status === 'success' ? (res?.data?.data || []) : [];
      setSourceBatchList(rows);
    }, { recipe_id: sourceMbmrId, recipe_type: recipeTypeFlag });
  };

  // Recipes shown in the dropdown: for BPR only packaging MBMRs (is_packaging),
  // for BMR only manufacturing MBMRs (non-packaging).
  const visibleRecipes = (recipe_data || []).filter((r) =>
    scheduleMode === 'BPR' ? r.is_packaging : !r.is_packaging
  );

  const recipeSelectOptions = useMemo(
    () => buildVersionedRecipeSelectOptions(visibleRecipes),
    [visibleRecipes]
  );

  // Whenever the "Source Master Batch" picker is shown (same condition as
  // that Col below), Batch Size isn't something to type — it's exactly the
  // yield of whichever completed upstream batch gets picked there.
  const sourceBatchDriven =
    (scheduleMode === 'BPR' || selected_recipe?.is_input_based_batch_size) && batch_type !== 'lot';

  const fetchBatchNumber = (recipe_id, setFieldValue) => {
    if (!recipe_id) return;
    const token = localStorage.getItem('Token');
    setBatchNumberLoading(true);
    Axios.get('/bp/generate_batch_number', {
      params: { recipe_id, recipe_type: recipeTypeFlag },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res?.data?.status === 'success') {
          setFieldValue('batch', res.data.data.batch_number);
        } else {
          toast.error(res?.data?.message || 'Failed to generate batch number');
        }
      })
      .catch(() => toast.error('Failed to generate batch number'))
      .finally(() => setBatchNumberLoading(false));
  };

  const getmachines = async (recipe_id) => {
    try {
      const params = recipe_id
        ? { recipe_id, recipe_type: recipeTypeFlag }
        : { recipe_type: recipeTypeFlag };
      await get_api_function(
        '/get_machine_list_with_submachines',
        success_bp_machine_data,
        params
      );
    } catch (error) {
      console.log(error);
    }
  };

  const success_bp_machine_data = (res) => {
    if (res.data.status !== 'failed') {
      const raw = (res?.data?.data || []).map((m) => ({
        ...m,
        machine_name: m.machine_name || m.name,
      }));
      // Sub-machines appear both as children AND as flat items — keep only top-level ones
      const subIds = new Set(raw.flatMap((m) => (m.sub_machines || []).map((s) => s.machine_id)));
      set_machine_data(raw.filter((m) => !subIds.has(m.machine_id)));
    }
  };

  const getDropDownData = async () => {
    try {
      await get_recipe_for_schedule({ module_type: selectedModule, recipe_type: recipeTypeFlag }, success_recipe_list_data);
    } catch (error) {
      console.log(error);
    }
  };

  const success_recipe_list_data = (res) => {
    const rows = res?.data?.status !== 'failed' ? res?.data?.data || [] : [];
    finalizedRecipeIdsRef.current = new Set(rows.map((r) => Number(r.recipe_id)));
    if (productRecipesRef.current.length) {
      set_recipe_data(
        filterFinalizedRecipes(productRecipesRef.current, finalizedRecipeIdsRef.current)
      );
    }
  };

  const handleViewBom = (recipe_id, batch_size, batch_number) => {
    if (!(batch_size && batch_size > 0)) {
      toast.warning('Please enter batch size to view BOM');
      return;
    }
    if (typeof setIsBomVisible === 'function' && typeof set_view_bom_data === 'function') {
      // Embedded: /batches owns the BOM modal (it also does lot picking there).
      setIsBomVisible(true);
      set_view_bom_data({ recipe_id: recipe_id, batch_size: batch_size, batch_number });
      return;
    }
    // Standalone: read-only material list from the same endpoint.
    setLocalBom({ open: true, loading: true, rows: [], batch_size });
    get_bill_of_materials({ recipe_id, batch_size, ...(batch_number ? { batch_number } : {}) }, (res) => {
      setLocalBom({
        open: true,
        loading: false,
        rows: res?.data?.data || [],
        batch_size,
      });
    });
  };

  const success_recipe_by_product = (res) => {
    if (res?.data?.status === 'success') {
      const rows = res?.data?.data || [];
      productRecipesRef.current = rows;
      set_recipe_data(filterFinalizedRecipes(rows, finalizedRecipeIdsRef.current));
    } else {
      productRecipesRef.current = [];
      set_recipe_data([]);
    }
  };

  const success_bp_product_data = (res) => {
    set_product_list(res?.data?.data);
  };

  const get_recipe_process_details_data = async (recipe_id, setFieldValue, values) => {
    try {
      await get_recipe_process_details({ recipe_id: recipe_id, recipe_type: recipeTypeFlag }, (res) => {
        if (res?.data?.status === 'success') {
          const newProcessList = res?.data?.data || [];

          // Preserve already selected roles if process_id still exists
          const updatedRoles = (values.process_roles || []).filter((role) =>
            newProcessList.some((proc) => proc.process_id === role.process_id)
          );

          setFieldValue('process_list_data', newProcessList);
          setFieldValue('process_roles', updatedRoles);
        } else {
          setFieldValue('process_list_data', []);
          setFieldValue('process_roles', []); // reset only when no process list
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    console.log(isESign);

    try {
      if (isESign) {
        setPendingBatchData({ values, resetForm });
        // Show e-signature modal
        setShowESignatureModal(true);
      } else {
        setPendingBatchData(null);
        submitBatchAfterSignature({ values, resetForm });
      }
    } catch (error) {
      console.error('Error preparing batch submission:', error);
    }
  };

  // 409 from an issue call = the server-side release gate refused. The body
  // carries either `release_gate` ({check_name: bool}) or a pre-flight
  // `items` ([{key, ok, detail}]) — render whichever is present as a check
  // list so the user can see WHICH check failed, not just that one did.
  // Purely presentational: nothing here bypasses the guard.
  const handleIssueError = ({ status, data } = {}) => {
    if (status !== 409) return;
    // The cleaning gate refused (services/execution/cleaning_gate.py): its lines name each machine.
    // Shown as a list with "Clean … →" per machine instead of one long toast.
    const lines = Array.isArray(data?.data?.machines) ? data.data.machines : null;
    if (lines && lines.some((l) => l && l.ok === false)) {
      toast.dismiss();
      setCleaningBlock({ message: 'This batch cannot be issued until its equipment is cleaned.', machines: lines.filter((l) => l && l.ok === false) });
      return;
    }
    const gate = data?.release_gate;
    const items = Array.isArray(data?.items)
      ? data.items
      : gate && typeof gate === 'object'
        ? Object.entries(gate).map(([key, ok]) => ({ key, ok: !!ok, detail: '' }))
        : [];
    if (!items.length) return; // plain 409 message — handleResponse already toasted it
    setReleaseGate({ message: data?.message || 'Release gate failed', items });
  };

  // Submit batch after successful e-signature
  const submitBatchAfterSignature = async (submitData) => {
    console.log(submitData);

    try {
      if (pendingBatchData || submitData) {
        const { values, resetForm } = pendingBatchData || submitData;
        console.log('check');

        if (batch_type === 'lot') {
          const payload = {
            ...values,
            batch_number: masterBatch?.batch_number,
            lot_number: values?.batch,
            lot_quantity: values?.batch_size,
            recipe_id: masterBatch?.recipe_id,
            // CPV-01 review: the purpose is checked with the market the screen asked about.
            ...(values?.order_market ? { market: values.order_market } : {}),
            selected_lots: selectedLots || {},
            // BP3: bulk batch the packaging lot packs (+ at-risk justification) — packaging recipes only
            ...packagingPayload(values),
          };
          batch_configuration_combine(
            payload,
            (res) => success_bp_batch_configuration(res, resetForm),
            { onError: handleIssueError }
          );
        } else {
          const allMachineIds = [
            ...(values.machine_id || []),
            ...(values.sub_machine_ids || []),
          ].map(Number);
          const payload = {
            ...values,
            machine_id: [...new Set(allMachineIds)],
            batch_number: values.batch,
            selected_lots: selectedLots || {},
            // BPR scheduling: flag + the source-MBMR batch this packages.
            // Same source_master_batch field also carries an input-based
            // recipe's selected upstream batch (see Source Master Batch above).
            schedule_mode: scheduleMode,
            is_packaging: scheduleMode === 'BPR',
            source_master_batch:
              scheduleMode === 'BPR' || selected_recipe?.is_input_based_batch_size
                ? (values.source_master_batch || null)
                : null,
            // Test/Production toggle from the main header.
            recipe_type: recipeTypeFlag,
            // Issue Batch: PO / SO / customer / market / window — stored on BatchOrderInfo
            order_info: orderPayload(values),
            // BP3 / BP4: bulk batch → packaging link (bulk_batch_number, bulk_units, at_risk_reason, change_control_ref)
            ...packagingPayload(values),
          };
          delete payload.sub_machine_ids;
          delete payload.machine_data;
          delete payload.packaging_link;
          bp_batch_configuration_pharma(
            payload,
            (res) => success_bp_batch_configuration(res, resetForm),
            { onError: handleIssueError }
          );
        }
      } else {
        return;
      }

      // Clear pending data
      setPendingBatchData(null);
    } catch (error) {
      console.error('Error submitting batch after signature:', error);
      toast.error('Failed to submit batch. Please try again.');
    }
  };

  const formikRef = useRef();

  useEffect(() => {
    if (formikRef.current && masterBatch) {
      get_recipe_process_details_data(
        masterBatch.recipe_id,
        formikRef.current.setFieldValue,
        formikRef.current.values
      );
    } else {
      set_initial_values({
        master_lot: false,
        product: '',
        recipe_id: '',
        batch: '',
        batch_size: '',
        machine_id: [],
        sub_machine_ids: [],
        machine_data: [],
        batch_type: batch_type,
        selectedModule: null,
        min_quantity: 0,
        continuous_monitoring: false,
        continuous_monitoring_parameter_ids: [],
        max_quantity: 0,
        remainning_size: 0,
        // process_roles: [],
        // process_list_data: [],
      });
    }
  }, [masterBatch]);

  const success_bp_batch_configuration = (res, resetForm) => {
    if (res.status === 'failed') {
      toast.warning(res.message);
    } else {
      toast.success('Batch added Successfully');
      resetForm();
      setSelectedMachineKeys([]);
      setReleaseGate(null);
      if (setSelectedLots) setSelectedLots({});
      // Only /batches has a list to refresh.
      if (typeof set_refresh === 'function') set_refresh(!refresh);
      if (onSuccess) onSuccess();
    }
  };

  useEffect(() => {
    // 2026-09-21 · clear BEFORE fetching, not after. `get_recipe()` is async: it started the
    // product fetch and then this same pass emptied `product_list`, so whichever finished last
    // won. On a fast response the clear landed second and the product dropdown came up empty —
    // the products were fetched, then thrown away. The clear is still right (a stale list from
    // the other module or the other side of the Test/Production toggle must not linger); it
    // just has to happen first.
    if (selectedModule !== 'pack') {
      set_recipe_data([]);
      set_product_list([]);
    }
    get_recipe();
    get_master_batchs_data();
    getDropDownData();
    // Re-fetch when the Test/Production toggle changes so recipe_type is applied.
  }, [selectedModule, effectiveTestProd]);

  useEffect(() => {
    if (batch_type === 'lot' && masterBatch?.recipe_id && formikRef?.current) {
      fetchBatchNumber(masterBatch.recipe_id, formikRef.current.setFieldValue);
    }
  }, [batch_type, masterBatch?.recipe_id]);

  const pageBody = (
    <>
      <Formik
        enableReinitialize
        innerRef={formikRef}
        initialValues={initial_values}
        validationSchema={
          batch_type === 'lot'
            ? Yup.object().shape({
              batch: Yup.string().required('Batch/Lot Number is required'),
              batch_purpose: Yup.string().nullable(), // 2026-10-07 · optional at issue
              batch_size: Yup.number()
                .nullable() // allow null initially
                .typeError('Lot Size must be a number')
                .required('Lot Size is required')
                .min(1, 'Lot Size must be greater than 0')
                .max(masterBatch?.remainning_size || 0, `Lot Size cannot exceed remaining size`),
              continuous_monitoring_parameter_ids: Yup.array().when('continuous_monitoring', {
                is: true,
                then: (s) => s.min(1, 'Select at least one monitoring parameter'),
                otherwise: (s) => s.nullable(),
              }),
              // process_roles: Yup.array().when("master_lot", {
              //   is: false,
              //   then: Yup.array()
              //     .of(
              //       Yup.object().shape({
              //         process_id: Yup.number().required(),
              //         role_id: Yup.array()
              //           .of(Yup.string().required("Invalid role"))
              //           .min(
              //             1,
              //             "At least one role is required for this process"
              //           )
              //           .required("Role is required for this process"),
              //       })
              //     )
              //     .test(
              //       "all-processes-assigned",
              //       "All processes must have assigned roles",
              //       function (value) {
              //         const { process_list_data } = this.parent;
              //         if (
              //           !process_list_data ||
              //           process_list_data.length === 0
              //         )
              //           return true;

              //         const assignedProcessIds = (value || []).map(
              //           (item) => item.process_id
              //         );
              //         const allProcessIds = process_list_data.map(
              //           (process) => process.process_id
              //         );

              //         return allProcessIds.every((processId) =>
              //           assignedProcessIds.includes(processId)
              //         );
              //       }
              //     ),
              //   otherwise: Yup.array(),
              // }),
            })
            : Yup.object().shape({
              product: Yup.string()
                .nullable()
                .when('selectedModule', {
                  is: 'prod',
                  then: Yup.string().required('Product is required'),
                  otherwise: Yup.string(),
                }),
              recipe_id: Yup.number()
                .nullable()
                .transform((value, originalValue) =>
                  originalValue === '' || originalValue == null ? null : Number(originalValue)
                )
                .required('Recipe is required'),
              batch: Yup.string().required('Batch/Lot Number is required'),
              batch_purpose: Yup.string().nullable(), // 2026-10-07 · optional at issue
              // schedule_time: Yup.string().nullable().required("Schedule Time is required"),
              // machine_id: Yup.string()
              //   .nullable()
              //   .when("batch_type", {
              //     is: (val) => val !== "lot",
              //     then: Yup.string(),
              //     otherwise: Yup.string(),
              //   }),
              batch_size: Yup.number()
                .nullable()
                .typeError('Batch Size must be a number')
                .required('Batch Size is required')
                .min(1, 'Batch Size must be greater than 0'),

              // process_roles: Yup.array().when("master_lot", {
              //   is: false,
              //   then: Yup.array()
              //     .of(
              //       Yup.object().shape({
              //         process_id: Yup.number().required(),
              //         role_id: Yup.array()
              //           .of(Yup.string().required("Invalid role"))
              //           .min(
              //             1,
              //             "At least one role is required for this process"
              //           )
              //           .required("Role is required for this process"),
              //       })
              //     )
              //     .test(
              //       "all-processes-assigned",
              //       "All processes must have assigned roles",
              //       function (value) {
              //         const { process_list_data } = this.parent;
              //         if (
              //           !process_list_data ||
              //           process_list_data.length === 0
              //         )
              //           return true;

              //         const assignedProcessIds = (value || []).map(
              //           (item) => item.process_id
              //         );
              //         const allProcessIds = process_list_data.map(
              //           (process) => process.process_id
              //         );

              //         return allProcessIds.every((processId) =>
              //           assignedProcessIds.includes(processId)
              //         );
              //       }
              //     ),
              //   otherwise: Yup.array(),
              // }),
              continuous_monitoring_parameter_ids: Yup.array().when('continuous_monitoring', {
                is: true,
                then: (s) => s.min(1, 'Select at least one monitoring parameter'),
                otherwise: (s) => s.nullable(),
              }),
            })
        }
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue, handleSubmit, errors, touched, handleReset, validateForm, setTouched }) => {
          // 2026-10-16 (owner) · "Load BOM from SAP" runs the same validation as Submit
          const validateForBom = async ({ touch } = {}) => {
            const errs = await validateForm();
            if (touch && errs && Object.keys(errs).length) {
              const mark = (o) => Object.fromEntries(Object.keys(o).map((k) => [k, (o[k] && typeof o[k] === 'object' && !Array.isArray(o[k])) ? mark(o[k]) : true]));
              setTouched(mark(errs), false);
            }
            return errs;
          };
          // Every field is declared once here and laid out twice below: embedded
          // (the Release-BMR / Release-Lot modal on /batches) keeps the original
          // single Row exactly as it was; standalone groups the same columns into
          // titled section cards inside the page shell.
          // Standalone sits in the left column of a two-column page, so its fields
          // pair up two-per-row at default height; the page must fit one screen.
          const colSpan = isStandalone ? { xs: 24, md: 12 } : { span: 6 };
          const wideSpan = isStandalone ? { span: 24 } : { span: 12 };
          const ctlSize = isStandalone ? 'middle' : 'large';
          const sectionTitle = (title, subtitle) => (
            <div style={{ padding: '2px 0' }}>
              <div style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px' }}>{title}</div>
              {subtitle && (
                <div style={{ fontWeight: 400, fontSize: 12, lineHeight: '16px', color: isDark ? '#94a3b8' : '#6b7280', whiteSpace: 'normal' }}>{subtitle}</div>
              )}
            </div>
          );
          const section = (title, subtitle, children, extra) => (
            <Card size="small" title={sectionTitle(title, subtitle)} extra={extra} style={{ marginBottom: 12 }}>
              {children}
            </Card>
          );
          const resetForm = () => {
            handleReset();
            if (batch_type === 'lot' && masterBatch?.recipe_id) {
              fetchBatchNumber(masterBatch.recipe_id, setFieldValue);
            }
          };
          const releaseTypePicker = (
            <>
            {batch_type !== 'lot' && (
              <div style={{ marginBottom: isStandalone ? 0 : 12 }}>
                {!isStandalone && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Record type
                  </div>
                )}
                <div style={{ display: 'inline-flex', gap: 8 }}>
                  {[
                    { value: 'BMR', title: 'BMR', sub: 'Manufacturing', Icon: Factory },
                    { value: 'BPR', title: 'BPR', sub: 'Packaging', Icon: Package },
                  ].map(({ value, title, sub, Icon }) => {
                    const active = scheduleMode === value;
                    return (
                      <div
                        key={value}
                        role="button"
                        onClick={() => {
                          if (active) return;
                          setScheduleMode(value);
                          // Reset recipe-dependent selections on mode switch.
                          setFieldValue('recipe_id', null);
                          setFieldValue('source_master_batch', null);
                          set_selected_recipe(undefined);
                          setSourceBatchList([]);
                          setFieldValue('batch', '');
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 12px',
                          borderRadius: 8,
                          cursor: active ? 'default' : 'pointer',
                          userSelect: 'none',
                          border: `1.5px solid ${active ? '#204448' : '#e5e7eb'}`,
                          background: active ? '#204448' : '#ffffff',
                          color: active ? '#ffffff' : '#374151',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Icon size={16} color={active ? '#ffffff' : '#204448'} />
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
                        <span style={{ fontSize: 11, opacity: active ? 0.8 : 0.55 }}>{sub}</span>
                        {active && <Check size={14} color="#ffffff" strokeWidth={3} style={{ marginLeft: 2 }} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </>
          );
          const colMasterBatch = (
            <>
              {batch_type === 'lot' && (
                <Col {...colSpan}>
                  <Form.Item
                    label="Master Batch"
                    validateStatus={touched.master_batch && errors.master_batch ? 'error' : ''}
                    help={touched.master_batch && errors.master_batch ? errors.master_batch : ''}
                  >
                    {/* <Select
                      showSearch
                      allowClear
                      disabled={batch_type === "lot"}
                      size={ctlSize}
                      placeholder="Select Batch"
                      value={values.master_batch || null}
                      onChange={(value) => {
                        setFieldValue("master_batch", value);
                        if (value) {
                          let val_name = value?.split("_");
                          const masterBatchData = batch_list?.find(
                            (mdata) => mdata?.batch_number === value
                          );
                          set_selected_master_batch_data(masterBatchData);
                          get_recipe_by_productid(masterBatchData?.product_id);
                          setFieldValue("product", masterBatchData?.product_id);
                          setFieldValue(
                            "recipe_id",
                            masterBatchData?.recipe_id
                          );
                          setFieldValue(
                            "batch",
                            `${val_name[val_name?.length - 1]
                              ?.trim()
                              ?.replace(/[^a-zA-Z0-9/_]/g, "")}_`
                          );
                        } else {
                          handleReset();
                        }
                      }}
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                    >
                      {batch_list?.map((mdata) => (
                        <Select.Option
                          key={mdata?.batch_number}
                          value={mdata?.batch_number}
                        >
                          {mdata.batch_number}
                        </Select.Option>
                      ))}
                    </Select> */}
                    <Input
                      placeholder="Master Lot"
                      disabled
                      value={masterBatch?.batch_number || ''}
                      style={{ backgroundColor: '#f5f5f5' }}
                      size={ctlSize}
                    />
                  </Form.Item>
                </Col>
              )}
            </>
          );
          const colProduct = (
            <>
              {selectedModule === 'prod' && (
                <Col {...colSpan}>
                  <Form.Item
                    label={selectedModule === 'prod' ? 'Product' : 'MBMR Product'}
                    validateStatus={touched.product && errors.product ? 'error' : ''}
                    help={touched.product && errors.product ? errors.product : ''}
                  >
                    {batch_type === 'lot' ? (
                      <Input
                        placeholder="Product"
                        disabled
                        value={masterBatch?.product_name || ''}
                        style={{ backgroundColor: '#f5f5f5' }}
                        size={ctlSize}
                      />
                    ) : (
                      <Select
                        showSearch
                        size={ctlSize}
                        placeholder="Select Product"
                        value={values.product || null}
                        onChange={(value) => {
                          setFieldValue('product', value);
                          setFieldValue('recipe_id', '');
                          get_recipe_by_productid(value);
                        }}
                        disabled={batch_type === 'lot'}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().includes(input?.toLowerCase())
                        }
                        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      >
                        {product_list?.map((mdata) => (
                          <Select.Option key={mdata.product_id} value={mdata.product_id}>
                            {mdata.product_name}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              )}
            </>
          );
          const colMbmr = (
            <>
              <Col {...colSpan}>
                <Form.Item
                  label="MBMR"
                  validateStatus={touched.recipe_id && errors.recipe_id ? 'error' : ''}
                  help={touched.recipe_id && errors.recipe_id ? errors.recipe_id : ''}
                >
                  {batch_type === 'lot' ? (
                    <Input
                      placeholder="MBMR"
                      disabled
                      size={ctlSize}
                      value={masterBatch?.recipe_name || ''}
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  ) : (
                    <Select
                      showSearch
                      allowClear
                      size={ctlSize}
                      placeholder="Select MBMR"
                      options={recipeSelectOptions}
                      optionLabelProp="label"
                      value={
                        values.recipe_id != null && values.recipe_id !== ''
                          ? Number(values.recipe_id)
                          : null
                      }
                      onChange={(value) => {
                        const recipeId = value != null ? Number(value) : null;
                        setFieldValue('recipe_id', recipeId);
                        setFieldValue('machine_data', []);
                        setSelectedMachineKeys([]);
                        set_machine_data([]);
                        const recipeData = resolveRecipeById(recipe_data, recipeId);
                        set_selected_recipe(recipeData);
                        get_recipe_process_details_data(recipeId, setFieldValue, values);
                        // Load machines filtered by this recipe's configured areas
                        if (recipeId) {
                          getmachines(recipeId);
                        }
                        // Fetch batch number from backend API
                        fetchBatchNumber(recipeId, setFieldValue);
                        const needsSourceBatch =
                          (scheduleMode === 'BPR' || recipeData?.is_input_based_batch_size) &&
                          batch_type !== 'lot';
                        if (needsSourceBatch) {
                          // Batch Size will come from whichever source master
                          // batch gets picked below — clear the stale value
                          // rather than showing the recipe's own (disabled,
                          // now-irrelevant) master batch size.
                          setFieldValue('batch_size', '');
                          applyBatchSize('');
                        } else if (recipeData?.master_batch_size) {
                          // Default batch size to recipe's actual quantity when recipe is selected
                          setFieldValue('batch_size', recipeData.master_batch_size);
                          applyBatchSize(recipeData.master_batch_size);
                        }
                        // Load completed batches of the upstream recipe for the
                        // Source Master Batch picker — BPR (packaging's source
                        // MBMR) or an input-based recipe's own "Select Recipe".
                        setFieldValue('source_master_batch', null);
                        if (needsSourceBatch) {
                          loadSourceBatches(recipeData);
                        }
                      }}
                      disabled={batch_type === 'lot'}
                      filterOption={(input, option) => {
                        if (option?.options) return false;
                        const label = option?.['data-label'] ?? option?.title ?? '';
                        return String(label).toLowerCase().includes(input.toLowerCase());
                      }}
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    />
                  )}
                </Form.Item>
              </Col>
            </>
          );
          const colSourceBatch = (
            <>
              {/* Source Master Batch — BPR (packaging's source MBMR) or an
                  input-based recipe (the recipe picked via "Select Recipe" in
                  its metadata). Either way: lists completed batches of that
                  upstream recipe — the batch this one packs/consumes. */}
              {(scheduleMode === 'BPR' || selected_recipe?.is_input_based_batch_size) && batch_type !== 'lot' && (
                <Col {...colSpan}>
                  <Form.Item label="Source Master Batch">
                    <Select
                      showSearch
                      allowClear
                      size={ctlSize}
                      placeholder="Select source master batch"
                      value={values.source_master_batch || null}
                      onChange={(val) => {
                        setFieldValue('source_master_batch', val || null);
                        // Batch Size isn't a number to type here — it's exactly
                        // the yield of whichever completed upstream batch gets
                        // picked, so pull it straight from that batch.
                        const picked = (sourceBatchList || []).find((b) => b.batch_number === val);
                        setFieldValue('batch_size', picked?.batch_size ?? '');
                        applyBatchSize(picked?.batch_size ?? '');
                      }}
                      disabled={!values.recipe_id}
                      notFoundContent={
                        !values.recipe_id
                          ? 'Select a recipe first'
                          : !selected_recipe?.source_mbmr_id
                            ? 'No upstream recipe configured for this recipe'
                            : 'No completed batches for the upstream recipe'
                      }
                      filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    >
                      {(sourceBatchList || []).map((b) => (
                        <Select.Option key={b.batch_number} value={b.batch_number}>
                          {b.batch_number}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
            </>
          );
          const colBatchNumber = (
            <>
              <Col {...colSpan}>
                <Form.Item
                  label={batch_type === 'master' ? 'Batch' : 'Lot Number'}
                  validateStatus={touched.batch && errors.batch ? 'error' : ''}
                  help={touched.batch && errors.batch ? errors.batch : ''}
                >
                  <Input
                    placeholder={batch_type === 'lot' ? 'Lot Number' : 'Batch'}
                    value={values.batch}
                    size={ctlSize}
                    // Editable only for master batches when the "Batch Number
                    // Editable" application setting is enabled; otherwise the
                    // auto-generated value is locked. Lot numbers stay locked.
                    disabled={!(isBatchNumberEditable && batch_type !== 'lot')}
                    onChange={(e) => setFieldValue('batch', e.target.value)}
                    style={
                      isBatchNumberEditable && batch_type !== 'lot'
                        ? undefined
                        : { backgroundColor: '#f5f5f5', color: '#333', cursor: 'not-allowed' }
                    }
                  />
                </Form.Item>
              </Col>
              {/* CPV-01 DD-CPV-21: what the batch is for — optional, options decided by the server */}
              <Col {...colSpan}>
                <BatchPurposeField
                  recipeId={batch_type === 'lot' ? masterBatch?.recipe_id : values.recipe_id}
                  market={values.order_market}
                  value={values.batch_purpose}
                  onChange={(v) => setFieldValue('batch_purpose', v)}
                  error={errors.batch_purpose}
                  touched={touched.batch_purpose}
                  size={ctlSize}
                />
              </Col>

              {/* <Col span={8}>
                <Form.Item
                  label="Schedule Time"
                  validateStatus={
                    errors.schedule_time && touched.schedule_time ? "error" : ""
                  }
                  help={
                    errors.schedule_time && touched.schedule_time
                      ? errors.schedule_time
                      : null
                  }
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    needConfirm={false}
                    value={
                      values.schedule_time ? dayjs(values.schedule_time) : null
                    }
                    onChange={(value) => {
                      setFieldValue(
                        "schedule_time",
                        value ? value.toISOString() : null
                      );
                    }}
                    style={{ width: "100%" }}
                    size={ctlSize}
                    disabledDate={(current) =>
                      current && current < dayjs().startOf("day")
                    }
                  />
                </Form.Item>
              </Col> */}
            </>
          );
          const colMachine = (
            <>
              {values.recipe_id && <Col {...colSpan}>
                <Form.Item
                  label="Machine"
                  validateStatus={touched.machine_id && errors.machine_id ? 'error' : ''}
                  help={touched.machine_id && errors.machine_id ? errors.machine_id : ''}
                >
                  <TreeSelect
                    placeholder="Select Machine(s) & Submachines"
                    size={ctlSize}
                    allowClear
                    multiple
                    treeCheckable
                    treeCheckStrictly
                    showCheckedStrategy={TreeSelect.SHOW_ALL}
                    treeDefaultExpandAll={false}
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    maxTagCount={selectedMachineKeys.includes('__all__') ? undefined : 'responsive'}
                    treeNodeFilterProp="title"
                    tagRender={(props) => {
                      const allSelected = selectedMachineKeys.includes('__all__');
                      // When All is selected, suppress every individual machine tag
                      if (allSelected && props.value !== '__all__') return null;
                      const isAll = props.value === '__all__';
                      return (
                        <span
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '0 4px 0 8px', margin: '2px 4px 2px 0',
                            lineHeight: '22px', borderRadius: 4, fontSize: 12,
                            background: isAll ? 'rgba(32,68,72,0.1)' : '#f5f5f5',
                            border: `1px solid ${isAll ? 'rgba(32,68,72,0.3)' : '#d9d9d9'}`,
                          }}
                        >
                          <span style={{ fontWeight: isAll ? 700 : 400, color: isAll ? '#204448' : undefined }}>
                            {isAll ? 'All' : props.label}
                          </span>
                          {props.closable && (
                            <span
                              onClick={(e) => { e.stopPropagation(); props.onClose(); }}
                              style={{ cursor: 'pointer', padding: '0 2px', opacity: 0.6, fontSize: 14 }}
                            >
                              ×
                            </span>
                          )}
                        </span>
                      );
                    }}
                    value={selectedMachineKeys.map((k) => ({ value: k, label: k }))}
                    treeData={[
                      {
                        title: <span style={{ fontWeight: 600, color: '#204448' }}>All</span>,
                        value: '__all__',
                        key: '__all__',
                      },
                      ...(machine_data || []).map((machine) => ({
                        title: machine?.machine_name || machine?.name,
                        value: `m-${machine?.machine_id}`,
                        key: `m-${machine?.machine_id}`,
                        children: (machine?.sub_machines || []).map((sub) => ({
                          title: sub?.name || `Machine #${sub?.machine_id}`,
                          value: `s-${sub?.machine_id}`,
                          key: `s-${sub?.machine_id}`,
                        })),
                      })),
                    ]}
                    onChange={(selected) => {
                      // treeCheckStrictly returns [{value, label}] — normalise to plain strings
                      const selectedKeys = (selected || []).map((s) => (typeof s === 'object' ? s.value : s));
                      const machines = machine_data || [];
                      const topLevelKeys = machines.map((machine) => `m-${machine.machine_id}`);
                      const subKeysByMain = new Map(
                        machines.map((machine) => [
                          `m-${machine.machine_id}`,
                          (machine.sub_machines || []).map((sub) => `s-${sub.machine_id}`),
                        ])
                      );
                      const allSubKeys = [...new Set(
                        [...subKeysByMain.values()].flat()
                      )];
                      const wasAll = selectedMachineKeys.includes('__all__');
                      const isAll = selectedKeys.includes('__all__');

                      let finalKeys;

                      if (!wasAll && isAll) {
                        // Selecting "All" selects every main machine and mapped submachine.
                        finalKeys = ['__all__', ...topLevelKeys, ...allSubKeys];
                      } else if (wasAll && !isAll) {
                        // Unchecked "All" → deselect everything
                        finalKeys = [];
                      } else {
                        const previousKeys = new Set(
                          selectedMachineKeys.filter((key) => key !== '__all__')
                        );
                        const nextKeys = new Set(
                          selectedKeys.filter((key) => key !== '__all__')
                        );

                        topLevelKeys.forEach((mainKey) => {
                          const wasSelected = previousKeys.has(mainKey);
                          const isSelected = nextKeys.has(mainKey);

                          if (!wasSelected && isSelected) {
                            // A newly selected main starts with all its children selected.
                            (subKeysByMain.get(mainKey) || []).forEach((subKey) => {
                              nextKeys.add(subKey);
                            });
                          } else if (wasSelected && !isSelected) {
                            // Keep a shared submachine when another selected main still owns it.
                            (subKeysByMain.get(mainKey) || []).forEach((subKey) => {
                              const belongsToAnotherSelectedMain = topLevelKeys.some(
                                (otherMainKey) =>
                                  otherMainKey !== mainKey &&
                                  nextKeys.has(otherMainKey) &&
                                  (subKeysByMain.get(otherMainKey) || []).includes(subKey)
                              );
                              if (!belongsToAnotherSelectedMain) nextKeys.delete(subKey);
                            });
                          }
                        });

                        const realKeys = [...nextKeys];
                        const allSelected =
                          topLevelKeys.length > 0 &&
                          topLevelKeys.every((key) => nextKeys.has(key)) &&
                          allSubKeys.every((key) => nextKeys.has(key));
                        finalKeys = allSelected ? ['__all__', ...realKeys] : realKeys;
                      }

                      finalKeys = [...new Set(finalKeys)];
                      setSelectedMachineKeys(finalKeys);

                      const keyList = finalKeys.filter((k) => k !== '__all__');
                      const machineIds = [...new Set(
                        keyList
                          .filter((key) => key.startsWith('m-'))
                          .map((key) => Number(key.slice(2)))
                      )];
                      const subMachineIds = [...new Set(
                        keyList
                          .filter((key) => key.startsWith('s-'))
                          .map((key) => Number(key.slice(2)))
                      )];

                      const combined = machineIds.map((mainId) => {
                        const mainMachine = (machine_data || []).find(
                          (m) => m?.machine_id === mainId
                        );
                        const validSubIds = (mainMachine?.sub_machines || [])
                          .map((s) => s?.machine_id)
                          .filter((sId) => subMachineIds.includes(sId));
                        return { machine_id: mainId, sub_machine_ids: validSubIds };
                      });

                      setFieldValue('machine_id', machineIds);
                      setFieldValue('sub_machine_ids', subMachineIds);
                      setFieldValue('machine_data', combined);
                    }}
                  />
                </Form.Item>
              </Col>}
            </>
          );
          const colBatchSize = (
            <>
              <Col {...colSpan}>
                <Form.Item
                  label={batch_type === 'master' ? 'Batch Size' : 'Lot Size'}
                  validateStatus={touched.batch_size && errors.batch_size ? 'error' : ''}
                  help={touched.batch_size && errors.batch_size ? errors.batch_size : ''}
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <InputNumber
                      placeholder={
                        sourceBatchDriven ? 'Yield of the selected source master batch' : 'Enter Batch Size'
                      }
                      style={{ width: '100%' }}
                      value={values.batch_size}
                      onChange={(value) => {
                        setFieldValue('batch_size', value);
                        applyBatchSize(value);
                      }}
                      size={ctlSize}
                      // Driven entirely by whichever completed upstream batch is
                      // picked in "Source Master Batch" above — not a number to
                      // type here (BPR's packed batch / an input-based recipe's
                      // consumed batch is exactly that batch's own yield).
                      // 2026-10-15 (owner) · a master batch is the MBMR's own size: filled in from the
                      // record's master batch size and not changeable here. A lot's size is still typed.
                      disabled={sourceBatchDriven || batch_type === 'master'}
                      // 2026-10-15 (owner) · the batch size with its unit (the record's own)
                      addonAfter={selected_recipe?.unit_name || undefined}
                      min={batch_type === 'lot' ? 0 : undefined}
                      max={
                        batch_type === 'lot' ? masterBatch?.remainning_size || undefined : undefined
                      }
                    />
                    {/* 2026-10-16 (owner) · no View BOM here: the BOM — with each material's AR, assay and
                        quantity to take — is shown by "Load BOM from SAP", where the batch is issued. */}
                  </Space.Compact>
                  {sourceBatchDriven && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Set from the selected Source Master Batch's yield
                    </Text>
                  )}
                  {!sourceBatchDriven && batch_type === 'master' && selected_recipe && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Master Batch Size: {selected_recipe.master_batch_size ?? '-'}{selected_recipe.unit_name ? ` ${selected_recipe.unit_name}` : ''}
                    </Text>
                  )}
                  {batch_type === 'lot' && masterBatch?.remainning_size && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Remaining Size: {masterBatch.remainning_size}
                    </Text>
                  )}
                </Form.Item>
              </Col>
            </>
          );
          const colToggles = (
            <>
              {(isMasterLotVisible && batch_type !== 'lot') && (
                <Col {...colSpan}>
                  <div className="flex items-center gap-5">
                    {isMasterLotVisible && batch_type !== 'lot' && (
                      <Form.Item
                        label="Master/Lot"
                        validateStatus={touched.master_lot && errors.master_lot ? 'error' : ''}
                        help={touched.master_lot && errors.master_lot ? errors.master_lot : ''}
                      >
                        <Switch
                          checked={values.master_lot}
                          onChange={(value) => {
                            setFieldValue('master_lot', value);
                            // Clear process_roles when switching to master
                            if (value) {
                              setFieldValue('process_roles', []);
                            }
                          }}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          style={{
                            backgroundColor: values.master_lot ? '#22c55e' : '#d1d5db',
                          }}
                        />
                      </Form.Item>
                    )}
                    {/* 2026-10-15 (owner) · Continuous Monitoring is not asked when a batch is issued —
                        it is authored on the MBMR (continuous monitoring across steps), not chosen here. */}
                  </div>
                </Col>
              )}
            </>
          );
          const colMonitoring = (
            <>
              {/* 2026-10-15 (owner) · the Continuous Monitoring toggle and its parameter picker are
                  gone from batch issue: continuous monitoring is authored on the MBMR. */}

              {/* Process-Roles Assignment Table */}
              {/* {values.master_lot === false &&
                values.process_list_data?.length > 0 && (
                  <Col span={24}>
                    <Card
                      title="Process Role Assignment"
                      style={{ marginBottom: 16 }}
                      size="small"
                    >
                      <DataTable
                        columns={getProcessRolesColumns(
                          values,
                          setFieldValue,
                          errors
                        )}
                        data={values.process_list_data}
                        rowKey="process_id"
                        pagination={false}
                        height={null}
                        bordered
                      />
                      {errors.process_roles &&
                        typeof errors.process_roles === "string" && (
                          <Text
                            type="danger"
                            style={{
                              fontSize: "12px",
                              marginTop: 8,
                              display: "block",
                            }}
                          >
                            {errors.process_roles}
                          </Text>
                        )}
                    </Card>
                  </Col>
                )} */}
            </>
          );
          // What was planned for the product being issued. Read-only, and honest about its limit:
          // the batch does not yet carry a demand reference, so this cannot show coverage
          // ("issued 2 of 3") — only what the demand board says is wanted.
          const matchedDemands = demandsForSelection(values.product, values.recipe_id);
          const colDemand = (
            <>
              {batch_type !== 'lot' && values.product && matchedDemands.length > 0 && (
                <Col span={24}>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={
                      matchedDemands.length === 1
                        ? 'Planned demand for this product'
                        : `${matchedDemands.length} planned demands for this product`
                    }
                    description={
                      <div style={{ fontSize: 12 }}>
                        {matchedDemands.map((d) => (
                          <div key={d.demand_id}>
                            <Text strong>Demand #{d.demand_id}</Text>
                            {` — ${d.required_quantity} required, ${d.start_date} to ${d.end_date}`}
                            {d.priority ? ` · ${d.priority} priority` : ''}
                            {` · ${d.status}`}
                          </div>
                        ))}
                        <div style={{ marginTop: 6 }}>
                          <Text type="secondary">
                            Shown for reference. This batch is not yet linked to a demand — the
                            batch record has no demand reference to store one in.
                          </Text>{' '}
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={() => navigate('/create-demand')}
                          >
                            Open the demand board
                          </Button>
                        </div>
                      </div>
                    }
                  />
                </Col>
              )}
            </>
          );
          const orderTitle = scheduleMode === 'BPR' ? 'Bulk batch, order & release metadata' : 'Order & release metadata';
          const orderFields = (
                    <OrderInfoFields values={values} setFieldValue={setFieldValue} dense={isStandalone}
                      markets={(selected_recipe?.product_markets || selected_recipe?.regulatory_config?.markets) || []}
                      shelfLifeMonths={selected_recipe?.shelf_life_months || selected_recipe?.product_shelf_life_months || null}
                      isPackaging={scheduleMode === 'BPR'} recipeId={values.recipe_id || selected_recipe?.recipe_id || null}
                      onPackagingLink={(link) => {
                        // the picked bulk batch is the Source Master Batch; batch size = theoretical packs (BP4)
                        setFieldValue('source_master_batch', link?.bulk_batch_number || null);
                        if (link?.theoretical_packs != null) { setFieldValue('batch_size', link.theoretical_packs); applyBatchSize(link.theoretical_packs); }
                      }} />
          );
          const colOrderInfo = (
            <>
              {batch_type !== 'lot' && (
                <Col span={24}>
                  <Card size="small" title={orderTitle} style={{ marginTop: 8, marginBottom: 8 }}>
                    {orderFields}
                  </Card>
                </Col>
              )}
            </>
          );

          // Embedded — byte-for-byte the layout this form has always had inside
          // the /batches modal: one Row of span-6 columns, Reset + Submit at the end.
          if (!isStandalone) {
            return (
              <Form layout="vertical">
                {releaseTypePicker}
                <Row gutter={16}>
                  {colMasterBatch}
                  {colProduct}
                  {colMbmr}
                  {colSourceBatch}
                  {colBatchNumber}
                  {colMachine}
                  {colBatchSize}
                  {colToggles}
                  {colMonitoring}
                  {colOrderInfo}
                  <Col span={24}>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                        <Button onClick={resetForm}>Reset</Button>
                        {/* 2026-10-16 (owner) · "Load BOM from SAP" until the BOM is imported; issued from its modal */}
                        <SapBomGate batchNumber={values.batch} batchSize={values.batch_size}
                                    recipeId={batch_type === 'lot' ? masterBatch?.recipe_id : values.recipe_id}
                                    submitLabel="Submit" onSubmit={() => handleSubmit()}
                                    validate={validateForBom} values={values}>
                          <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => {
                              handleSubmit();
                            }}
                          >
                            Submit
                          </Button>
                        </SapBomGate>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            );
          }

          // Standalone (/scheduling, "Issue Batch") — the same fields grouped into
          // section cards, laid out in two columns so the whole form fits one
          // screen without scrolling: what is issued (record type, batch,
          // machines) on the left, order & release metadata on the right.
          return (
            <Form layout="vertical" className="issue-batch-form">
              <Row gutter={12} className="issue-batch-cols">
                <Col xs={24} xl={10} className="issue-batch-col">
                  {section(
                    batch_type === 'lot' ? 'Lot' : 'Batch',
                    'The approved master record this batch is issued from, its number and its size.',
                    <Row gutter={12}>
                      {colMasterBatch}
                      {colProduct}
                      {colMbmr}
                      {colSourceBatch}
                      {colBatchNumber}
                      {colBatchSize}
                      {colToggles}
                      {colMonitoring}
                      {colDemand}
                    </Row>,
                    batch_type !== 'lot' ? releaseTypePicker : null
                  )}
                  {section(
                    'Machines & schedule',
                    values.recipe_id
                      ? 'Only machines configured on the selected master record are listed.'
                      : 'Select a master record above to list the equipment this batch may run on.',
                    values.recipe_id ? (
                      <Row gutter={12}>{colMachine}</Row>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>No machines to pick yet.</Text>
                    )
                  )}
                </Col>
                {batch_type !== 'lot' && (
                  <Col xs={24} xl={14} className="issue-batch-col">
                    {section(orderTitle, 'Stored on the batch, shown in its header and report, frozen once the batch starts.', orderFields)}
                  </Col>
                )}
              </Row>
              {/* Action bar at the end of the form (not sticky — the window
                  scrolls, not the layout box, so sticky never engages). */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 8,
                  paddingTop: 10,
                  marginTop: 'auto',
                  borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#e5e7eb'}`,
                }}
              >
                <Text type="secondary" style={{ fontSize: 12, marginRight: 'auto' }}>
                  {values.batch ? `Batch number ${values.batch}` : 'Batch number is assigned when a master record is selected.'}
                </Text>
                <Button onClick={() => navigate('/batches')}>Cancel</Button>
                <Button onClick={resetForm}>Reset</Button>
                {/* 2026-10-16 (owner) · "Load BOM from SAP" until the BOM is imported; issued from its modal */}
                <SapBomGate batchNumber={values.batch} batchSize={values.batch_size}
                            recipeId={batch_type === 'lot' ? masterBatch?.recipe_id : values.recipe_id}
                            icon={<PackageCheck size={16} />}
                            submitLabel={batch_type === 'lot' ? 'Issue lot' : scheduleMode === 'BPR' ? 'Issue packaging batch' : 'Issue batch'}
                            onSubmit={() => handleSubmit()}
                            validate={validateForBom} values={values}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<PackageCheck size={16} />}
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    {batch_type === 'lot'
                      ? 'Issue lot'
                      : scheduleMode === 'BPR'
                        ? 'Issue packaging batch'
                        : 'Issue batch'}
                  </Button>
                </SapBomGate>
              </div>
            </Form>
          );
        }}
      </Formik>

      {/* E-Signature Modal - Basic + 21 CFR Certificate */}
      <DualESignatureAuth
        visible={showESignatureModal}
        onCancel={() => {
          setShowESignatureModal(false);
          setPendingBatchData(null);
        }}
        onSubmit={(signatureData) => {
          submitBatchAfterSignature();
          setShowESignatureModal(false);
          setPendingBatchData(null);
        }}
        signatureType="batch_schedule"
        action_type="batch_schedule"
        signatureMeaning={batch_type === 'lot' ? 'Lot Scheduling' : 'Batch Scheduling'}
        documentType="BATCH"
        batch_number={pendingBatchData?.values?.batch || ''}
        recipe_id={pendingBatchData?.values?.recipe_id || ''}
        documentId={pendingBatchData?.values?.recipe_id || ''}
        documentNumber={pendingBatchData?.values?.batch || ''}
        documentData={{
          batch_number: pendingBatchData?.values?.batch || '',
          recipe_id: pendingBatchData?.values?.recipe_id || '',
          batch_size: pendingBatchData?.values?.batch_size || 0,
          batch_type: batch_type,
          module: selectedModule,
        }}
        config={{
          enableBasic: true,
          enableCFR: true,
          defaultMode: 'cfr',
          requireComment: true,
          title: 'E-Signature for Batch Scheduling',
        }}
      />

      {/* Bill of Materials — standalone only. /batches owns a richer BOM modal
          (it also does lot picking there) and passes the setters down; here the
          same endpoint is shown read-only so "View BOM" still works. */}
      <Modal
        title="Bill of Materials"
        open={localBom.open}
        onCancel={() => setLocalBom((s) => ({ ...s, open: false }))}
        footer={null}
        width={760}
      >
        <DataTable
          height={null}
          loading={localBom.loading}
          data={localBom.rows}
          rowKey={(r) => r?.material_id ?? r?.material_name}
          columns={[
            { title: 'Material', dataIndex: 'material_name', key: 'material_name' },
            {
              title: 'Quantity',
              dataIndex: 'quantity',
              key: 'quantity',
              align: 'right',
              // Same per-unit × batch-size arithmetic the /batches BOM modal uses.
              render: (v, r) =>
                // 2026-10-15 · the server's quantity for this batch size, where it gives one
                `${r?.batch_quantity != null ? +Number(r.batch_quantity).toFixed(4)
                  : (parseFloat(v || 0) * parseFloat(localBom.batch_size || 0)).toFixed(3)} ${r?.unit || ''}`,
            },
            { title: 'Type', dataIndex: 'material_type', key: 'material_type', align: 'center' },
          ]}
          pagination={{ pageSize: 8 }}
          bordered
        />
      </Modal>

      {/* Release gate — the server refused the issue with 409 and a check list.
          Shown as-is; the guard itself lives (and stays) on the backend. */}
      <Modal
        open={!!cleaningBlock}
        onCancel={() => setCleaningBlock(null)}
        title="Equipment not clean — this batch cannot be issued yet"
        width={680}
        footer={<Button onClick={() => setCleaningBlock(null)}>Close</Button>}
      >
        <Alert type="error" showIcon message={cleaningBlock?.message} style={{ marginBottom: 12 }} />
        {(cleaningBlock?.machines || []).map((l) => (
          <div key={l.machine_id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between',
                                           padding: '10px 0', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{l.machine || `Machine #${l.machine_id}`}
                {l.state ? <span style={{ marginLeft: 8, fontSize: 12.5, color: '#cf1322' }}>{String(l.state).replace(/_/g, ' ').toLowerCase()}</span> : null}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.6)' }}>{l.reason}</div>
            </div>
            {l.machine_id ? (
              <Button type="primary" size="small" style={{ flexShrink: 0 }}
                      onClick={() => { setCleaningBlock(null); navigate(`/equipment-rooms?equipment=${l.machine_id}&clean=1`); }}>
                Clean {l.machine || 'machine'} →
              </Button>
            ) : null}
          </div>
        ))}
      </Modal>
      <Modal
        title="Release gate — this batch cannot be issued yet"
        open={!!releaseGate}
        onCancel={() => setReleaseGate(null)}
        footer={<Button onClick={() => setReleaseGate(null)}>Close</Button>}
        width={620}
      >
        <Alert type="error" showIcon message={releaseGate?.message} style={{ marginBottom: 12 }} />
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {(releaseGate?.items || []).map((it) => (
            <li key={it.key} style={{ marginBottom: 4, color: it.ok ? '#16a34a' : '#dc2626' }}>
              <span style={{ fontWeight: 600 }}>{it.ok ? '✓' : '✗'} {it.key}</span>
              {it.detail ? <span style={{ color: '#6b7280' }}> — {it.detail}</span> : null}
            </li>
          ))}
        </ul>
      </Modal>

      <style>{`
        .monitoring-params-select .ant-select-selector {
          max-height: 116px;
          overflow-y: auto;
        }
        /* Standalone page fills the viewport between the app header (48px)
           and footer (28px) plus the card's own 10px margins; the columns and
           their last card stretch to the bottom, the action bar sits under them. */
        .issue-batch-page {
          display: flex;
          flex-direction: column;
          min-height: calc(100vh - 96px);
        }
        .issue-batch-page > .ant-card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .issue-batch-form {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .issue-batch-cols {
          flex: 1;
          margin-bottom: 12px;
        }
        .issue-batch-col {
          display: flex;
          flex-direction: column;
        }
        .issue-batch-col > .ant-card:last-child {
          flex: 1;
          margin-bottom: 0 !important;
        }
        .issue-batch-form .ant-form-item {
          margin-bottom: 10px;
        }
        .issue-batch-form .ant-form-item-label {
          padding-bottom: 2px;
        }
        .issue-batch-form .ant-card-head {
          min-height: 0;
          padding-top: 6px;
          padding-bottom: 6px;
        }
      `}</style>
    </>
  );

  // Embedded in the /batches modal — unchanged: the bare form, no page chrome.
  if (!isStandalone) return <div>{pageBody}</div>;

  // Standalone "Issue Batch" page. Uses MainCard like every other page in the app —
  // it already carries the border, shadow, page margin and dark-mode surface, so this
  // page has no shell of its own to drift out of step. The form spans the full card
  // width so its two columns fit one screen.
  return (
    <MainCard
      className="issue-batch-page"
      title={
        <PageHeader
          compact
          icon={<PackageCheck size={22} />}
          title="Issue Batch"
          description="Issue a new batch from an approved MBMR/MBPR — assigns the batch number, size, order metadata and machines, and creates the executable BMR."
        />
      }
    >
      {pageBody}
    </MainCard>
  );
}

export default Scheduling;
